import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import buildUrlWithAppId from '/business/shared/functions/buildUrlWithAppId';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import { urlApplicationRoot } from '/business/shared/util/Statics';

const appendPath = (basePath: string, path = '') => {
  if (!path) {
    return basePath;
  }

  if (path.startsWith('?') || path.startsWith('#')) {
    return `${basePath}${path}`;
  }

  return path.startsWith('/') ? `${basePath}${path}` : `${basePath}/${path}`;
};

export default function useUrlBuilder() {
  const { search } = useLocation();
  const activeApplicationId = useSelector(getApplicationIdSelector);
  const urlApplicationId = new URLSearchParams(search).get('appId');
  const defaultApplicationId = activeApplicationId ?? urlApplicationId;

  const getUrlWithAppId = useCallback(
    (to: string, appId: string | number | null | undefined = defaultApplicationId) => {
      return buildUrlWithAppId(to, appId);
    },
    [defaultApplicationId],
  );

  const getApplicationUrl = useCallback(
    (path = '', appId: string | number | null | undefined = defaultApplicationId) => {
      return getUrlWithAppId(appendPath(`/${urlApplicationRoot}`, path), appId);
    },
    [defaultApplicationId, getUrlWithAppId],
  );

  const getApplicationSettingsUrl = useCallback(
    (path = '', appId: string | number | null | undefined = defaultApplicationId) => {
      return getUrlWithAppId(appendPath(`/${urlApplicationRoot}-settings`, path), appId);
    },
    [defaultApplicationId, getUrlWithAppId],
  );

  return {
    getApplicationSettingsUrl,
    getApplicationUrl,
    getUrlWithAppId,
  };
}
